/**
 * Choosing a username, with a live availability check.
 *
 * The name becomes the student's web address, so the field shows exactly what that
 * address will be. Taken or lookalike names come back with free alternatives, which
 * fill the field with one click.
 */

import { Field, Input, Spinner } from '@praxadon/ui';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

import { type UsernameCheck, onboarding } from '../../api/endpoints.ts';

export function UsernameField({
  value,
  onChange,
  serverError,
  suggestions: serverSuggestions,
}: {
  value: string;
  onChange: (value: string) => void;
  serverError?: string | undefined;
  suggestions?: string[] | undefined;
}) {
  // The answer is kept with the name it was for, so it disappears the moment the
  // field changes rather than describing a name that is no longer there.
  const [answer, setAnswer] = useState<{ name: string; check: UsernameCheck } | null>(null);
  const [checking, setChecking] = useState(false);
  const check = answer?.name === value.trim() ? answer.check : null;

  useEffect(() => {
    const trimmed = value.trim();
    if (trimmed.length < 3) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setChecking(true);
      onboarding
        .checkUsername(trimmed, controller.signal)
        .then(
          (result) => {
            setAnswer({ name: trimmed, check: result });
          },
          () => {
            /* the submit will report anything real */
          },
        )
        .finally(() => {
          setChecking(false);
        });
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  const address = `${check?.available ? check.username : value.trim().toLowerCase() || 'yourname'}.${window.location.hostname}`;
  const error = serverError ?? (check && !check.available ? check.message : undefined);
  const suggestions =
    serverSuggestions ?? (check && !check.available ? check.suggestions : undefined);

  return (
    <div className="flex flex-col gap-2">
      <Field
        label="Username"
        error={error}
        hint={
          <>
            Your site will live at <span className="font-mono text-fg-muted">{address}</span>
          </>
        }
      >
        <Input
          name="username"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={63}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      </Field>
      <div className="flex min-h-5 items-center gap-2 text-xs" aria-live="polite">
        {checking ? (
          <span className="flex items-center gap-1 text-fg-subtle">
            <Spinner size={12} /> Checking…
          </span>
        ) : check?.available ? (
          <span className="flex items-center gap-1 text-success">
            <Check size={12} aria-hidden /> Available
          </span>
        ) : null}
      </div>
      {suggestions && suggestions.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
          Try:
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => {
                onChange(suggestion);
              }}
              className="rounded-full border border-border px-2 py-0.5 font-mono text-fg hover:border-accent hover:bg-accent-soft"
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
